<template>
	<div class="booker-car-delivery">
		<Transition name="fade" mode="out-in">
			<div v-if="!searchFieldDelivery" class="booker-car-delivery__delivery-map">
				<span class="booker-car-delivery__delivery-map-image-wrapper">
					<GlobalImage
						:src="'/delivery-map.svg'"
						:alt="$t('components.booker.car_delivery.how_it_works')"
						class="booker-car-delivery__delivery-map-image" />
					<div class="booker-car-delivery__delivery-map-content">
						<div class="booker-car-delivery__delivery-map-title">{{ $t('components.booker.car_delivery.how_it_works') }}</div>
						<GlobalListOrdered :data="type === EBookerLocationType.START ? mapPointsStart : mapPointsEnd" />
					</div>
				</span>
			</div>
			<template v-else-if="results">
				<div class="booker-car-delivery__content" :style="{ '--offset': locationOffset }">
					<Transition name="fade" mode="out-in">
						<div v-if="searchingHistory && searchingHistory.length > 0" class="booker-car-delivery__localization">
							<div class="booker-car-delivery__title">{{ $t('components.booker.car_delivery.history') }}</div>
							<v-list class="booker-car-delivery__locations">
								<GlobalBookerLocation
									v-for="(item, i) in searchingHistoryFiltered.slice(-3).map((item) => {
										return { ...item, icon: 'location' }
									})"
									:key="i"
									:item="item"
									@click="selectLocation(item)" />
							</v-list>
						</div>
					</Transition>
					<div v-if="results.length > 0" class="booker-car-delivery__localization">
						<div class="booker-car-delivery__title">
							{{
								type === EBookerLocationType.START
									? $t('components.booker.car_delivery.select_pickup_address')
									: $t('components.booker.car_delivery.select_dropoff_address')
							}}
						</div>
						<v-list class="booker-car-delivery__locations">
							<GlobalBookerLocation
								v-for="(item, i) in resultsSorted(results)"
								:key="i"
								:item="item"
								:disabled="!isLocationSelectable(item)"
								@click="selectLocation(item)" />
						</v-list>
					</div>
					<div v-else-if="results.length === 0" class="booker-car-delivery__localization">
						<div class="booker-car-delivery__info">
							{{ $t('components.booker.car_delivery.no_results') }} „{{ searchFieldDelivery }}”.
						</div>
						<v-divider class="booker-car-delivery__divider"></v-divider>
						<div class="booker-car-delivery__title">{{ $t('components.booker.car_delivery.improve_search') }}</div>
						<GlobalListPoints :data="noDataPoints" />
					</div>
				</div>
			</template>
			<div v-else-if="results === undefined" class="booker-car-delivery__content">
				<div class="booker-car-delivery__loader">
					<v-progress-circular indeterminate color="var(--primary-color)"></v-progress-circular>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script lang="ts" setup>
import { EBookerLocationType } from '@/data/bookerData'
const tab = inject<Ref<string>>('tab')!
const searchFieldDelivery = inject<Ref<string | undefined>>('searchFieldDelivery')!
const reservationStore = useReservationStore()
const headerRef = ref<HTMLElement | null>(null)
const isDeliveryCarModalOpen = inject<Ref<boolean>>('isDeliveryCarModalOpen')!
const bookerState = inject<Ref<IBookerState>>('bookerState')!
provide('headerRef', headerRef)
const bookingStore = useBookingStore()
const searchRef = ref<HTMLElement | null>(null)
const props = defineProps<{
	type: EBookerLocationType
}>()

const results = inject<Ref<any[]>>('results') || ref([])
const inputRef = inject<Ref<any>>('inputRef')!
const { isMobile } = useWindowWidth()
const bookerStore = useBookerStore()

watch(
	() => results.value,
	(newValue) => {
		console.log('results', newValue)
	},
	{ immediate: true },
)

// watch(
// 	() => bookerState.value.selectedLocations[props.type]?.placeID,
// 	(newValue) => {
// 		if (newValue) {
// 			setTimeout(() => {
// 				searchFieldDelivery.value = bookerState.value.selectedLocations[props.type]?.name
// 			}, 100)
// 		}
// 	},
// 	{ immediate: true },
// )

const locationOffset = computed(() => {
	const heights = (headerRef.value?.clientHeight ?? 0) + (searchRef.value?.clientHeight ?? 0)
	return `${heights}px`
})

const mapPointsStart = [
	{
		text: 'enter_your_address',
	},
	{
		text: 'substitution_time',
	},
	{
		text: 'deliver_to_your_door',
	},
]

const mapPointsEnd = [
	{
		text: 'enter_your_address',
	},
	{
		text: 'pickup_time',
	},
	{
		text: 'pickup_to_your_door',
	},
]

const noDataPoints = [{ text: 'check_your_spelling_again' }, { text: 'use_fewer_words' }, { text: 'cant_find_the_city', intercom: true }]

const history = ref()
const searchingHistory = computed<ILocationModel[]>(() => {
	if (!searchFieldDelivery.value) return history.value
	const search = searchFieldDelivery.value.toLowerCase()
	const locations = reservationStore.getLocationDeliveryHistory
	return locations.filter((location) => {
		if (!location) return false
		return location?.name?.toLowerCase()?.includes(search)
	})
})

const selectLocation = (item: ILocationModel) => {
	bookerStore.setDeliveryCosts(props.type, null)
	bookerState.value.selectedLocations[props.type] = {
		name: item.description || item.name,
		placeID: item.place_id || item.placeID,
		isDelivery: true,
	}
	reservationStore.addLocationDeliveryToHistory(item.place_id || item.placeID, item.description)
	getPlaceDetails(item.place_id || item.placeID)
	bookingStore.calculateDeliveryPrice(item.place_id || item.placeID, props.type)
	if (props.type === EBookerLocationType.START) {
		if (!isMobile.value) tab.value = 'summary'
		else if (isMobile.value && !bookerState.value.otherEndLocation) isDeliveryCarModalOpen.value = true
		else if (isMobile.value && bookerState.value.otherEndLocation) {
			bookerState.value.isStartLocationMenuOpen = false
			bookerState.value.isEndLocationMenuOpen = true
		}
	} else if (props.type === EBookerLocationType.END) {
		if (!isMobile.value) tab.value = 'summary'
		else if (isMobile.value) isDeliveryCarModalOpen.value = true
	}
	bookingStore.setBookerLocations(bookerState.value.selectedLocations)
	bookingStore.updateBooking()
}
const filterHistory = (data: any[]) => {
	if (data) {
		history.value = reservationStore.getLocationHistory.slice(-3)
	}
}

watch(
	() => reservationStore.getLocationDeliveryHistory,
	(data) => {
		if (data) {
			filterHistory(data)
		}
	},
	{ immediate: true, deep: true },
)

const searchingHistoryFiltered = computed(() => {
	return searchingHistory.value.filter((obj, index, self) => index === self.findIndex((t) => t?.placeID === obj?.placeID))
})

const addressForm = ref<ILocationAddress>({
	email: '',
	street: '',
	city: '',
	postCode: '',
	country: '',
	houseNumber: '',
	googleCoordinates: {
		lat: undefined,
		lng: undefined,
	},
})

const selectedAddress = ref()
const Autocomplete = ref()
const AutocompleteService = ref()
const AutocompletePlace = ref()

const displaySuggestions = (predictions, status) => {
	if (status === google.maps.places.PlacesServiceStatus.OK) {
		console.log('predictions', predictions)
		results.value = predictions.map((prediction) => {
			return { ...prediction, name: prediction.description, icon: 'location' }
		})
	} else {
		results.value = []
	}
}

const getPlaceDetails = (placeId) => {
	AutocompletePlace.value.getDetails({ placeId, fields: ['address_components', 'geometry'] }, function (place, status) {
		if (status === google.maps.places.PlacesServiceStatus.OK) {
			fillInAddress(place)
		} else {
			console.error('Place details request failed due to ' + status)
		}
	})
}

const { locale } = useI18n()

watch(
	() => searchFieldDelivery.value,
	(newValue) => {
		if (
			newValue &&
			newValue.length >= 1 &&
			!Autocomplete.value &&
			inputRef.value &&
			process.client &&
			window?.google &&
			google?.maps &&
			google?.maps?.places
		) {
			// const inputGoogleElement = inputRef.value.$el.querySelector('#inputGoogle')
			// Autocomplete.value = new google.maps.places.Autocomplete(inputGoogleElement)
			AutocompleteService.value = new google.maps.places.AutocompleteService()
			AutocompletePlace.value = new google.maps.places.PlacesService(document.createElement('div'))
		}
		if (newValue && newValue.length >= 1 && AutocompleteService.value) {
			AutocompleteService.value.getPlacePredictions(
				{
					input: newValue,
					componentRestrictions: { country: 'pl' },
					types: ['geocode'],
					language: locale.value,
				},
				displaySuggestions,
			)
		}
	},
	{ immediate: true },
)

const fillInAddress = (place: any) => {
	selectedAddress.value = place
	let street = ''
	let postcode = ''

	for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
		const componentType = component.types[0]

		switch (componentType) {
			case 'street_number': {
				addressForm.value.houseNumber = `${component.long_name} `
				break
			}

			case 'route': {
				street = `${component.long_name}`
				break
			}

			case 'postal_code': {
				postcode = `${component.long_name}${postcode}`
				break
			}

			case 'postal_code_suffix': {
				postcode = `${postcode}-${component.long_name}`
				break
			}

			case 'locality':
				addressForm.value.city = component.long_name
				break

			case 'country':
				addressForm.value.country = component.long_name
				break
		}
	}

	addressForm.value.street = street
	addressForm.value.postCode = postcode
	addressForm.value.googleCoordinates = {
		lat: place.geometry.location.lat(),
		lng: place.geometry.location.lng(),
	}
}

const isLocationSelectable = (item: any) => {
	const types = item.types

	if (types.includes('street_address') || types.includes('premise') || types.includes('subpremise')) {
		return true
	}
	return false
}

const resultsSorted = (results: any[]) => {
	// return results sorted by types
	// first subpremise, then premise, then street_address

	const sortedResults = results.sort((a, b) => {
		const aTypes = a.types
		const bTypes = b.types
		if (aTypes.includes('subpremise') && !bTypes.includes('subpremise')) return -1
		if (!aTypes.includes('subpremise') && bTypes.includes('subpremise')) return 1
		if (aTypes.includes('premise') && !bTypes.includes('premise')) return -1
		if (!aTypes.includes('premise') && bTypes.includes('premise')) return 1
		if (aTypes.includes('street_address') && !bTypes.includes('street_address')) return -1
		if (!aTypes.includes('street_address') && bTypes.includes('street_address')) return 1
		return 0
	})
	return sortedResults
}
</script>

<style lang="scss">
@import './CarDelivery';
</style>
